/**
 * A person without a family (spec 2026-10-02 upacara bersama, Part B). Family is optional; a
 * lingkungan then is the person's own (`umat.lingkungan_id`, which a later family replaces, as
 * it always follows the household), and so is an address: the six columns a family's address
 * has. The server's rule: a person row never ends with neither a family nor a lingkungan; a row
 * that already had neither (stored before the rule, or a person a mutasi moved between families)
 * may still change its other columns. Pure: the push route, Tambah umat and the ceremony batch
 * read it.
 */
import { type LocalWrite, uuidv7 } from './sync/ops'

/** The person's own address, the family's six address columns (Kampung, Distrik on screen). */
export const KOLOM_ALAMAT_UMAT = [
  'alamat',
  'rt_rw',
  'kampung_desa',
  'kecamatan',
  'kabupaten_kota',
  'provinsi',
] as const
export type KolomAlamatUmat = (typeof KOLOM_ALAMAT_UMAT)[number]

const kosong = (v: unknown): boolean =>
  v === null || v === undefined || (typeof v === 'string' && v.trim() === '')

/** The row names a family or a lingkungan. */
export function punyaTempat(row: Readonly<Record<string, unknown>>): boolean {
  return !kosong(row.keluarga_id) || !kosong(row.lingkungan_id)
}

/**
 * The server's rule on a person row (`sebelum` the stored row, null for a creation; `sesudah`
 * the row as the op leaves it): a deletion passes; a creation or a revival must name a family or
 * a lingkungan; a change may not empty both, unless the row already had neither.
 */
export function tempatUmatSah(
  sebelum: Readonly<Record<string, unknown>> | null,
  sesudah: Readonly<Record<string, unknown>>,
): boolean {
  if (!kosong(sesudah.dihapus_pada)) return true
  if (punyaTempat(sesudah)) return true
  const baru = sebelum === null || !kosong(sebelum.dihapus_pada)
  return !baru && !punyaTempat(sebelum)
}

/** No street, no kampung and no distrik of their own: a KTP's address may fill it. */
export function alamatSendiriKosong(row: Readonly<Record<string, unknown>>): boolean {
  return kosong(row.alamat) && kosong(row.kampung_desa) && kosong(row.kecamatan)
}

export interface UmatTanpaKeluargaInput {
  parokiId: string | null
  lingkunganId: string
  /** The person's columns: Data diri's, what a reading added, their own address. */
  kolom: Readonly<Record<string, unknown>>
  newId?: (() => string) | undefined
}

/**
 * A new person without a family: one umat row in the lingkungan, no membership row. The server's
 * rule is kept here first (Tambah umat and the ceremony batch both write through this): without a
 * lingkungan nothing is built, so a push is never refused for it.
 */
export function tulisanUmatTanpaKeluarga(input: UmatTanpaKeluargaInput): {
  write: LocalWrite
  umatId: string
} {
  const fields = {
    paroki_id: input.parokiId,
    status_gereja: 'katolik',
    status_hidup: 'hidup',
    ...input.kolom,
    lingkungan_id: input.lingkunganId,
    keluarga_id: null,
    hubungan: null,
  }
  if (!tempatUmatSah(null, fields)) {
    throw new Error('tulisanUmatTanpaKeluarga: a person without a family needs a lingkungan')
  }
  const umatId = (input.newId ?? uuidv7)()
  return { umatId, write: { table: 'umat', row_id: umatId, fields } }
}
